import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import {
  Users, FileText, CheckCircle, Clock, AlertTriangle, Send, 
  Loader2, BarChart3, UserCheck, UserX, ChevronDown, ChevronUp,
  Calendar, Bell, X, RefreshCw
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Progress } from './ui/progress';
import { toast } from 'sonner';

export default function PolicyAcknowledgementManager({ policyId, policyTitle, onClose }) {
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [assigning, setAssigning] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);
  
  // Assignment modal state
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignToAll, setAssignToAll] = useState(true);
  const [dueDays, setDueDays] = useState(14);
  const [assignNotes, setAssignNotes] = useState('');
  
  // Sections visibility
  const [showAcknowledged, setShowAcknowledged] = useState(false);
  const [showPending, setShowPending] = useState(true);
  const [showOverdue, setShowOverdue] = useState(true);

  useEffect(() => {
    fetchReport();
  }, [policyId]);

  const fetchReport = async () => {
    try {
      const response = await axios.get(`${API}/policies/${policyId}/acknowledgements`);
      setReport(response.data);
    } catch (error) {
      console.error('Failed to fetch acknowledgement report:', error);
      toast.error('Failed to load acknowledgement data');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignPolicy = async () => {
    setAssigning(true);
    try {
      const response = await axios.post(`${API}/policies/${policyId}/assign`, {
        policy_id: policyId,
        assign_to_all: assignToAll,
        due_days: dueDays,
        notes: assignNotes || null
      });
      
      toast.success(response.data.message);
      setAssignModalOpen(false);
      fetchReport();
    } catch (error) {
      console.error('Failed to assign policy:', error);
      toast.error(error.response?.data?.detail || 'Failed to assign policy');
    } finally {
      setAssigning(false);
    }
  };

  const handleSendReminders = async () => {
    setSendingReminders(true);
    try {
      const response = await axios.post(`${API}/policies/${policyId}/send-reminders`);
      toast.success(response.data.message);
      fetchReport();
    } catch (error) {
      console.error('Failed to send reminders:', error);
      toast.error(error.response?.data?.detail || 'Failed to send reminders');
    } finally {
      setSendingReminders(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-slate-600" />
      </div>
    );
  }

  const complianceRate = report?.compliance_rate || 0;

  return (
    <div className="space-y-6" data-testid="policy-ack-manager">
      {/* Header Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-white border-slate-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-slate-100">
                <Users className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-slate-900">{report?.total_assigned || 0}</p>
                <p className="text-sm text-slate-600">Total Assigned</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border-slate-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100">
                <UserCheck className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-green-600">{report?.acknowledged_count || 0}</p>
                <p className="text-sm text-slate-600">Acknowledged</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border-slate-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-100">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-amber-600">{report?.pending_count || 0}</p>
                <p className="text-sm text-slate-600">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border-slate-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-100">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-red-600">{report?.overdue_count || 0}</p>
                <p className="text-sm text-slate-600">Overdue</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Compliance Progress */}
      <Card className="bg-white border-slate-200">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-700">Compliance Rate</span>
            <span className={`text-lg font-semibold ${
              complianceRate >= 80 ? 'text-green-600' : 
              complianceRate >= 50 ? 'text-amber-600' : 'text-red-600'
            }`}>
              {complianceRate}%
            </span>
          </div>
          <Progress 
            value={complianceRate} 
            className={`h-3 ${
              complianceRate >= 80 ? '[&>div]:bg-green-500' : 
              complianceRate >= 50 ? '[&>div]:bg-amber-500' : '[&>div]:bg-red-500'
            }`}
          />
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <Button
          onClick={() => setAssignModalOpen(true)}
          className="bg-slate-900 hover:bg-slate-800 text-white"
          data-testid="assign-policy-btn"
        >
          <Users className="w-4 h-4 mr-2" />
          Assign to Users
        </Button>
        
        {(report?.pending_count > 0 || report?.overdue_count > 0) && (
          <Button
            onClick={handleSendReminders}
            disabled={sendingReminders}
            variant="outline"
            className="border-amber-300 text-amber-700 hover:bg-amber-50"
            data-testid="send-reminders-btn"
          >
            {sendingReminders ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Bell className="w-4 h-4 mr-2" />
            )}
            Send Reminders ({(report?.pending_count || 0) + (report?.overdue_count || 0)})
          </Button>
        )}
        
        <Button
          onClick={fetchReport}
          variant="outline"
          className="border-slate-300"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Overdue Users */}
      {report?.overdue?.length > 0 && (
        <Card className="bg-white border-red-200">
          <CardHeader 
            className="cursor-pointer"
            onClick={() => setShowOverdue(!showOverdue)}
          >
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg text-red-700 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Overdue ({report.overdue.length})
              </CardTitle>
              {showOverdue ? <ChevronUp className="w-5 h-5 text-red-600" /> : <ChevronDown className="w-5 h-5 text-red-600" />}
            </div>
          </CardHeader>
          {showOverdue && (
            <CardContent>
              <div className="space-y-2">
                {report.overdue.map((ack) => (
                  <div 
                    key={ack.id}
                    className="flex items-center justify-between p-3 bg-red-50 rounded-lg"
                  >
                    <div>
                      <p className="font-medium text-slate-900">{ack.user_name}</p>
                      <p className="text-sm text-slate-600">{ack.user_email}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-red-600">Due: {formatDate(ack.due_date)}</p>
                      {ack.reminder_count > 0 && (
                        <p className="text-xs text-slate-500">{ack.reminder_count} reminder(s) sent</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* Pending Users */}
      {report?.pending?.length > 0 && (
        <Card className="bg-white border-amber-200">
          <CardHeader 
            className="cursor-pointer"
            onClick={() => setShowPending(!showPending)}
          >
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg text-amber-700 flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Pending ({report.pending.length})
              </CardTitle>
              {showPending ? <ChevronUp className="w-5 h-5 text-amber-600" /> : <ChevronDown className="w-5 h-5 text-amber-600" />}
            </div>
          </CardHeader>
          {showPending && (
            <CardContent>
              <div className="space-y-2">
                {report.pending.map((ack) => (
                  <div 
                    key={ack.id}
                    className="flex items-center justify-between p-3 bg-amber-50 rounded-lg"
                  >
                    <div>
                      <p className="font-medium text-slate-900">{ack.user_name}</p>
                      <p className="text-sm text-slate-600">{ack.user_email}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-amber-600">Due: {formatDate(ack.due_date)}</p>
                      {ack.reminder_count > 0 && (
                        <p className="text-xs text-slate-500">{ack.reminder_count} reminder(s) sent</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* Acknowledged Users */}
      {report?.acknowledged?.length > 0 && (
        <Card className="bg-white border-green-200">
          <CardHeader 
            className="cursor-pointer"
            onClick={() => setShowAcknowledged(!showAcknowledged)}
          >
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg text-green-700 flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                Acknowledged ({report.acknowledged.length})
              </CardTitle>
              {showAcknowledged ? <ChevronUp className="w-5 h-5 text-green-600" /> : <ChevronDown className="w-5 h-5 text-green-600" />}
            </div>
          </CardHeader>
          {showAcknowledged && (
            <CardContent>
              <div className="space-y-2">
                {report.acknowledged.map((ack) => (
                  <div 
                    key={ack.id}
                    className="flex items-center justify-between p-3 bg-green-50 rounded-lg"
                  >
                    <div>
                      <p className="font-medium text-slate-900">{ack.user_name}</p>
                      <p className="text-sm text-slate-600">{ack.user_email}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-green-600">Acknowledged: {formatDate(ack.acknowledged_at)}</p>
                      {ack.signature && (
                        <p className="text-xs text-slate-500">Signed: {ack.signature}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* Assign Modal */}
      <Dialog open={assignModalOpen} onOpenChange={setAssignModalOpen}>
        <DialogContent className="bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="text-slate-900 flex items-center gap-2">
              <Users className="w-5 h-5" />
              Assign Policy for Acknowledgement
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              Assign "{policyTitle}" to users for acknowledgement.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium text-slate-900">Assign to All Organization Members</p>
                <p className="text-sm text-slate-600">
                  All current members will be required to acknowledge this policy
                </p>
              </div>
              <Switch
                checked={assignToAll}
                onCheckedChange={setAssignToAll}
                data-testid="assign-all-switch"
              />
            </div>

            <div>
              <Label htmlFor="due-days" className="text-slate-700">Days Until Due</Label>
              <Input
                id="due-days"
                type="number"
                value={dueDays}
                onChange={(e) => setDueDays(parseInt(e.target.value) || 14)}
                min={1}
                max={90}
                className="mt-1 bg-white border-slate-300"
                data-testid="due-days-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Users will have {dueDays} days to acknowledge the policy
              </p>
            </div>

            <div>
              <Label htmlFor="assign-notes" className="text-slate-700">Notes (optional)</Label>
              <Input
                id="assign-notes"
                value={assignNotes}
                onChange={(e) => setAssignNotes(e.target.value)}
                placeholder="e.g., Annual policy review required"
                className="mt-1 bg-white border-slate-300"
                data-testid="assign-notes-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAssignModalOpen(false)}
              className="border-slate-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAssignPolicy}
              disabled={assigning}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="confirm-assign-btn"
            >
              {assigning ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Assigning...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Assign Policy
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
